// src/pages/ContactPage.jsx (or adjust path as needed)
"use client";

import { useState } from "react";
import { Helmet } from "react-helmet";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Header } from "../components/Header"; // Adjust path if needed
import Footer from "../components/Footer"; // Adjust path if needed

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // 'idle', 'submitting', 'success', 'error'
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError(null);

    try {
      console.log("Submitting form data:", formData);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // --- Example: Using Fetch API (replace with your endpoint) ---
      // const response = await fetch('/api/contact', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData),
      // });
      //
      // if (!response.ok) {
      //   const errorData = await response.json();
      //   throw new Error(errorData.message || 'Something went wrong');
      // }
      // --- End Example ---

      // If submission is successful:
      setStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" }); // Clear form
      console.log("Form submitted successfully!");

    } catch (err) {
      console.error("Submission failed:", err);
      setError(err.message || "Failed to send message. Please try again later.");
      setStatus("error");
    }
    // --- End FORM SUBMISSION LOGIC ---
  };

  const metaTitle = "Contact Us | HotelHero.fun";
  const metaDescription = "Get in touch with the HotelHero.fun team. Send us a message, ask a question, or provide feedback using our contact form.";

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {/* Add other relevant meta tags like Open Graph if needed */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content="https://hotelhero.fun/contact" /> {/* Adjust URL */}
        <link rel="canonical" href="https://hotelhero.fun/contact" /> {/* Adjust URL */}
      </Helmet>

       {/* Using the gradient background like the blog page */}
       <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-[100dvh] text-white">
        <div className="container mx-auto px-4 pt-8 pb-20">
          <Header /> {/* Include your standard header */}
        </div>

        {/* Page Content Area - White background for contrast */}
        <main className="grow bg-white pb-16 md:pb-24">
          <div className="container max-w-5xl mx-auto px-4 sm:px-6 pt-12 md:pt-16">
            {/* Page Header */}
            <div className="text-center mb-12 md:mb-16">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Get In Touch
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Have questions, feedback, or just want to say hello? We'd love to hear from you! Fill out the form below or use our contact details.
              </p>
            </div>

            {/* Grid for Form and Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">

              {/* Contact Form Section */}
              <div className="bg-gray-50 p-6 sm:p-8 rounded-lg shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#7200B8] focus:border-[#7200B8] transition duration-150 ease-in-out text-black"
                      placeholder="e.g. Jane Doe"
                      disabled={status === 'submitting'}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#7200B8] focus:border-[#7200B8] transition duration-150 ease-in-out text-black"
                      placeholder="you@example.com"
                      disabled={status === 'submitting'}
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      id="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#7200B8] focus:border-[#7200B8] transition duration-150 ease-in-out text-black"
                      placeholder="What is this about?"
                      disabled={status === 'submitting'}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      name="message"
                      id="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-[#7200B8] focus:border-[#7200B8] transition duration-150 ease-in-out text-black"
                      placeholder="Write your message here..."
                      disabled={status === 'submitting'}
                    />
                  </div>

                  {/* Status Messages */}
                  <div className="h-6"> {/* Reserve space to prevent layout shifts */}
                    {status === 'success' && (
                      <p className="flex items-center text-sm text-green-600" role="alert">
                        <CheckCircle size={16} className="mr-2" />
                        Message sent successfully! We'll get back to you soon.
                      </p>
                    )}
                    {status === 'error' && (
                      <p className="flex items-center text-sm text-red-600" role="alert">
                         <AlertCircle size={16} className="mr-2" />
                        {error || "An error occurred. Please try again."}
                      </p>
                    )}
                  </div>


                  <div>
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className={`w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#7200B8] hover:bg-[#5a0094] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7200B8] transition duration-150 ease-in-out ${status === 'submitting' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {status === 'submitting' ? (
                        <>
                          <Loader2 size={20} className="animate-spin -ml-1 mr-3" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} className="-ml-1 mr-2" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Contact Details Section */}
              <div className="text-gray-900">
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Alternatively, you can reach us through the following channels. We aim to respond within 24-48 business hours.
                </p>
                <div className="space-y-5">
                  <div className="flex items-start">
                    <Mail size={20} className="mr-4 mt-1 text-[#7200B8] shrink-0" />
                    <div>
                      <h3 className="font-semibold">Email</h3>
                      <a href="mailto:hi@olympikesoft.com" className="text-gray-600 hover:text-[#7200B8] hover:underline">
                      hi@olympikesoft.com {/* Replace with your actual email */}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </main>
       </div> 

      <Footer /> {/* Include your standard footer */}
    </>
  );
}